import { useMutation } from '@tanstack/react-query';
import { LoginParams, MessageResponse, ResetPasswordParams, TokenResponse } from '@zond/api-interfaces';
import { AxiosError } from 'axios';
import { baseURL, client } from '../../../client';

export const useLogin = () => {
  return useMutation<TokenResponse, AxiosError, LoginParams>({
    mutationFn: async (data: LoginParams) => {
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/0a7b4581-49f6-4684-8632-077228ed366c', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          location: 'auth.queries.ts:LOGIN_START',
          message: 'Login request initiated',
          data: {
            email: data.email,
            baseURL,
            fullUrl: `${baseURL}/auth/login`,
            viteApiUrl: import.meta.env.VITE_API_URL,
          },
          timestamp: Date.now(),
          sessionId: 'debug-session',
          hypothesisId: 'C',
        }),
      }).catch(() => {});
      // #endregion
      try {
        const res = await client.post(`/auth/login`, data);
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/0a7b4581-49f6-4684-8632-077228ed366c', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            location: 'auth.queries.ts:LOGIN_SUCCESS',
            message: 'Login request succeeded',
            data: { status: res.status },
            timestamp: Date.now(),
            sessionId: 'debug-session',
            hypothesisId: 'A',
          }),
        }).catch(() => {});
        // #endregion
        return res.data;
      } catch (error) {
        // #region agent log
        const axiosError = error as AxiosError;
        fetch('http://127.0.0.1:7242/ingest/0a7b4581-49f6-4684-8632-077228ed366c', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            location: 'auth.queries.ts:LOGIN_ERROR',
            message: 'Login request failed',
            data: {
              errorMessage: axiosError.message,
              errorCode: axiosError.code,
              responseStatus: axiosError.response?.status,
              hasResponse: !!axiosError.response,
              isNetworkError: axiosError.message === 'Network Error',
            },
            timestamp: Date.now(),
            sessionId: 'debug-session',
            hypothesisId: 'A',
          }),
        }).catch(() => {});
        // #endregion
        throw error;
      }
    },
  });
};

export const useVerifyEmail = () => {
  return useMutation<MessageResponse, AxiosError, string>({
    mutationFn: (token: string) => client.post(`/auth/verify-email`, { token }).then((res) => res.data),
  });
};

export const useResetPassword = () => {
  return useMutation<MessageResponse, AxiosError, ResetPasswordParams>({
    mutationFn: (data: ResetPasswordParams) =>
      client.post(`/auth/reset-password`, data).then((res) => res.data),
  });
};

export const useRequestPasswordReset = () => {
  return useMutation<MessageResponse, AxiosError, string>({
    mutationFn: (email: string) =>
      client.post(`/auth/request-password-reset`, { email }).then((res) => res.data),
  });
};

interface UpdatePasswordParams {
  password: string;
  passwordConfirmation: string;
}

export const useUpdatePassword = () => {
  return useMutation<MessageResponse, AxiosError, UpdatePasswordParams>({
    mutationFn: (data: UpdatePasswordParams) =>
      client.post(`/auth/update-password`, data).then((res) => res.data),
  });
};
